import React from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { ATHLETES, SESSIONS, ACTIVITY_LABEL, OrgRole, isOrgManager, formatTime } from "../mockData";
import { StatusBadge } from "../components/StatusBadge";
import { Sparkline } from "../components/StatCard";
import { ArrowLeft, ChevronRight, Mail, Calendar, MessageCircle } from "lucide-react";

export default function RosterMember() {
  const { id } = useParams();
  const { role } = useOutletContext<{ role: OrgRole }>();
  const isManager = isOrgManager(role);
  const member = ATHLETES.find((a) => a.id === id);
  const sessions = SESSIONS.filter((s) => s.athleteId === id);

  if (!member) {
    return (
      <div className="card p-8 text-center text-slate-500">
        Not found.{" "}
        <Link to="/roster" className="text-el-700">Back to roster</Link>
      </div>
    );
  }
  const processed = sessions.filter((s) => s.status === "processed");
  const avgScore =
    processed.length === 0
      ? 0
      : Math.round(processed.reduce((a, s) => a + (s.score ?? 0), 0) / processed.length);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Link to="/roster" className="inline-flex items-center gap-1 hover:text-slate-700">
          <ArrowLeft className="h-3.5 w-3.5" />
          {isManager ? "Clients" : "Friends"}
        </Link>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        <span className="text-slate-700 font-medium">{member.name}</span>
      </div>

      <div className="card p-5">
        <div className="flex flex-wrap items-start gap-5">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-el-300 to-el-600 text-white text-xl font-semibold flex items-center justify-center">
            {member.initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold tracking-tightish text-slate-900">{member.name}</h2>
              <span className="badge bg-el-50 text-el-700 ring-1 ring-el-200">{member.activity}</span>
              {!isManager && (
                <span className="badge bg-slate-100 text-slate-600 ring-1 ring-slate-200">Friend</span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                {member.name.toLowerCase().replace(/[^a-z]/g, "")}@eleetz.app
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> Last seen {member.lastSeen}
              </span>
              <span>· {member.team ?? "Independent"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isManager ? (
              <>
                <button className="btn-secondary !h-9">Add note</button>
                <button className="btn-primary  !h-9">Share AI report</button>
              </>
            ) : (
              <>
                <button className="btn-secondary !h-9">
                  <MessageCircle className="h-4 w-4" /> Cheer
                </button>
                <button className="btn-primary !h-9">Compare swings</button>
              </>
            )}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3 max-w-md">
          <Mini label="Sessions"      value={member.sessions} />
          <Mini label="Avg quality"   value={avgScore} suffix="/100" />
          <Mini label="Last 8 trend"  value={<Sparkline values={member.trend} />} />
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-900">
            {isManager ? "Session history" : "Recent activity"}
          </div>
          <div className="text-xs text-slate-500">{sessions.length} total</div>
        </div>
        {sessions.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-slate-500">No sessions yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sessions.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/sessions/${s.id}`}
                  className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50 transition"
                >
                  <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center text-xl">
                    {s.thumb}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{ACTIVITY_LABEL[s.activity]}</div>
                    <div className="text-xs text-slate-500">
                      {formatTime(s.capturedAt)} · {s.durationSec}s · {s.id}
                    </div>
                  </div>
                  {s.status === "processed" && (
                    <div className="hidden md:flex items-center gap-2 w-32">
                      <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-el-400 to-el-600"
                          style={{ width: `${s.score}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-700 font-medium">{s.score}</span>
                    </div>
                  )}
                  <StatusBadge status={s.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value, suffix }: { label: string; value: React.ReactNode; suffix?: string }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2.5">
      <div className="text-[0.65rem] uppercase tracking-[0.06em] text-slate-500 font-semibold">
        {label}
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <div className="text-lg font-semibold text-slate-900">{value}</div>
        {suffix && <div className="text-[0.7rem] text-slate-500">{suffix}</div>}
      </div>
    </div>
  );
}
